import {
  Dispatch,
  FC,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { IAuthParams, ILoggedInUser } from '../apis/auth/auth.interfaces';
import { useAuthUserHook } from '../hooks/useAuthUserHook';

type TAuthUserAppState = {
  errorMessage?: string;
  isLoading: boolean;
};

enum ACTION_TYPE {
  SET_USER_PARAMS = 'SET_USER_PARAMS', // required for logging in the user
  RESET_USER_DETAILS = 'RESET_USER_DETAILS', // required for logout
  FETCHING_DATA = 'FETCHING_DATA',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

type Action =
  | { type: ACTION_TYPE.SET_USER_PARAMS; payload: IAuthParams }
  | { type: ACTION_TYPE.RESET_USER_DETAILS; payload: ILoggedInUser }
  | { type: ACTION_TYPE.FETCHING_DATA; payload: boolean }
  | { type: ACTION_TYPE.FETCH_DATA_FAILURE; payload: string };

const initialAuthUserState: TAuthUserAppState = {
  errorMessage: undefined,
  isLoading: false,
};

type TState = {
  loggedUserAppState: TAuthUserAppState;
  userDetails: ILoggedInUser;
  userParams: IAuthParams;
};

const AuthUserContext = createContext<TState | undefined>(undefined);
const AuthUserDispatchContext = createContext<Dispatch<Action> | undefined>(
  undefined
);

const AuthUserReducer = (
  appState: TAuthUserAppState,
  action: Action
): TAuthUserAppState => {
  switch (action.type) {
    case ACTION_TYPE.RESET_USER_DETAILS:
      return { ...appState, ...action.payload };
    case ACTION_TYPE.SET_USER_PARAMS:
      return { ...appState, ...action.payload };
    case ACTION_TYPE.FETCHING_DATA:
      return { ...appState, isLoading: action.payload, errorMessage: '' };
    case ACTION_TYPE.FETCH_DATA_FAILURE:
      return { ...appState, isLoading: false, errorMessage: action.payload };
    default:
      throw new Error('Unhandled action type');
  }
};

export const AuthUserProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [loggedUserAppState, dispatch] = useReducer(
    AuthUserReducer,
    initialAuthUserState
  );

  const loading = false;
  const success = true;
  const error = false;

  useEffect(() => {
    onStateChangeHandler(loading, success, error);
    // eslint-disable-next-line
  }, [loading, success, error]);

  const onStateChangeHandler = (
    loading: boolean,
    success: boolean,
    error: boolean
  ) => {
    if (loading) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
    } else if (success) {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    } else if (error) {
      dispatch({
        type: ACTION_TYPE.FETCH_DATA_FAILURE,
        payload: 'This entire useEffect needs rework',
      });
    }
  };

  const currentState = useMemo<TState>(
    () => ({
      loggedUserAppState,
      userDetails: {} as ILoggedInUser,
      userParams: {} as IAuthParams,
    }),
    [loggedUserAppState]
  );

  return (
    <AuthUserDispatchContext.Provider value={dispatch}>
      <AuthUserContext.Provider value={currentState}>
        {children}
      </AuthUserContext.Provider>
    </AuthUserDispatchContext.Provider>
  );
};

export const useAuthUserState = () => {
  const state = useContext(AuthUserContext);
  if (state === undefined) {
    throw new Error('useAuthUserState must be used within a AuthUserProvider');
  }
  return state;
};

export const useAuthUserActions = () => {
  const dispatch = useContext(AuthUserDispatchContext);
  if (!dispatch) {
    throw new Error(
      'useAuthUserActions dispatch must be within a AuthUserProvider'
    );
  }

  const authoriseUserDetails = useAuthUserHook();

  // --------------------- initiate CRUD APIs ---------------------
  const handleApiCall = async (apiCall: any): Promise<void> => {
    try {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: true });
      await apiCall(); // Call the API function
    } catch (error) {
      if (error instanceof Error) {
        dispatch({
          type: ACTION_TYPE.FETCH_DATA_FAILURE,
          payload: error?.message,
        });
      }
    } finally {
      dispatch({ type: ACTION_TYPE.FETCHING_DATA, payload: false });
    }
  };

  const loginUserApiCall = async (params: IAuthParams) => {
    return await handleApiCall(() => authoriseUserDetails.mutate(params));
  };

  return { loginUserApiCall };
};
